import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useAuthApi } from "../hooks/useAuthApi";
import { PLATFORM } from "../env";

interface ActiveSessionSeriesPoint {
  timestamp: string;
  active_sessions: number;
}

function ActiveSessionsTimeSeriesChart({ points }: { points: ActiveSessionSeriesPoint[] }) {
  const width = 860;
  const height = 260;
  const padding = { top: 18, right: 24, bottom: 40, left: 52 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  const maxY = Math.max(1, ...points.map((point) => point.active_sessions));
  const ticks = [0, Math.ceil(maxY / 2), maxY];
  const lastIndex = Math.max(points.length - 1, 1);

  const polyline = points
    .map((point, index) => {
      const x = padding.left + (index / lastIndex) * plotWidth;
      const y = padding.top + ((maxY - point.active_sessions) / maxY) * plotHeight;
      return `${x},${y}`;
    })
    .join(" ");

  const xLabel = (index: number) => new Date(points[index].timestamp).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
        Y-axis: Sessions
      </Typography>
      <Box component="svg" viewBox={`0 0 ${width} ${height}`} sx={{ width: "100%", height: "auto" }}>
        {ticks.map((tick) => {
          const y = padding.top + ((maxY - tick) / maxY) * plotHeight;
          return (
            <g key={tick}>
              <line x1={padding.left} x2={width - padding.right} y1={y} y2={y} stroke="currentColor" opacity={0.2} />
              <text x={padding.left - 8} y={y + 4} textAnchor="end" fontSize="11" fill="currentColor">
                {tick}
              </text>
            </g>
          );
        })}
        <polyline fill="none" stroke="currentColor" strokeWidth="2.5" points={polyline} />
        {[0, Math.floor(lastIndex / 2), lastIndex].map((index) => {
          const x = padding.left + (index / lastIndex) * plotWidth;
          return (
            <text key={`${index}-${x}`} x={x} y={height - 12} textAnchor="middle" fontSize="11" fill="currentColor">
              {xLabel(index)}
            </text>
          );
        })}
      </Box>
    </Box>
  );
}

export default function Dashboard() {
  const api = useAuthApi();
  const [points, setPoints] = useState<ActiveSessionSeriesPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const series = await api<ActiveSessionSeriesPoint[]>(
          `${PLATFORM}/transactions/active-timeseries?hours=24&bucket_minutes=60`
        );
        setPoints(series ?? []);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [api]);

  const activeNow = useMemo(() => points[points.length - 1]?.active_sessions ?? 0, [points]);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Active transactions (last 24h)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Current active sessions: {activeNow}
        </Typography>
        {loading ? (
          <Box sx={{ minHeight: 220, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : points.length < 2 ? (
          <Typography color="text.secondary">No session activity yet.</Typography>
        ) : (
          <ActiveSessionsTimeSeriesChart points={points} />
        )}
      </CardContent>
    </Card>
  );
}
